import React from 'react';

import { CoreHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { getSavedTokenDataFromStorage } from '@helpers';
import { useLogger } from '@hooks';
import { Routes, subscriptionFlowPathCompany, subscriptionFlowPathIziBizi } from '@navigation';
import { useNavigationStep, useNavigationStore } from '@store';

export interface WizardItem {
	title: string;
	children?: WizardItem[];
	route?: Routes;
}

interface WizardMenuHelperOutput {
	wizardMenuItems: WizardItem[];
	selectedIndex: number;
	totalChildrenCount: (index: number) => number;
	totalParentWithChildrenCount: (index: number) => number;
}

export const useWizardMenuHelper = (): WizardMenuHelperOutput => {
	const t = useTranslation();
	const logger = useLogger();
	const { current } = useNavigationStep();
	const { flow } = useNavigationStore();
	const [selectedIndex, setSelectedIndex] = React.useState<number>(-1);

	const state = getSavedTokenDataFromStorage<string>('state');
	const wizardMenuItems = React.useMemo((): WizardItem[] => {
		return CoreHelper.isNullOrEmpty(state)
			? [
					{
						title: t('placeholders.wizardMenu.paymentOptions'),
						route: 'subscriptionPaymentOptions',
					},
					{
						title: t('placeholders.wizardMenu.selectAccount'),
						route: 'subscriptionSelectAccount',
					},
					{
						title: t('placeholders.wizardMenu.corporateData'),
						route: 'subscriptionCorporateData',
					},
					{
						title: t('placeholders.wizardMenu.integratorData'),
						route: 'subscriptionIntegratorData',
					},
					{
						title: t('placeholders.wizardMenu.termsAndConditions'),
						route: 'subscriptionTermsAndConditionsBank',
					},
					{
						title: t('placeholders.wizardMenu.resumeAndAuthorization'),
						route: 'subscriptionResumeAndAuthorization',
					},
					{
						title: t('placeholders.wizardMenu.conclusion'),
						route: 'subscriptionConclusion',
					},
			  ]
			: [
					{
						title: t('placeholders.wizardMenu.paymentOptions'),
						route: 'subscriptionPaymentOptions',
					},
					{
						title: t('placeholders.wizardMenu.selectAccount'),
						route: 'subscriptionSelectAccount',
					},
					{
						title: t('placeholders.wizardMenu.corporateData'),
						route: 'subscriptionCorporateData',
					},

					{
						title: t('placeholders.wizardMenu.termsAndConditions'),
						route: 'subscriptionTermsAndConditionsBank',
					},
					{
						title: t('placeholders.wizardMenu.resumeAndAuthorization'),
						route: 'subscriptionResumeAndAuthorization',
					},
					{
						title: t('placeholders.wizardMenu.conclusion'),
						route: 'subscriptionConclusion',
					},
			  ];
	}, [t, state]);

	const findSelectedIndex = React.useCallback(
		(currentRoute: string) => {
			let index: number;
			if (CoreHelper.isNullOrEmpty(state)) {
				index = subscriptionFlowPathCompany.findIndex((route) => route === currentRoute);
				setSelectedIndex(index);
			} else {
				index = subscriptionFlowPathIziBizi.findIndex((route) => route === currentRoute);
				setSelectedIndex(index);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[flow]
	);

	const totalChildrenCount = (index: number) => {
		return wizardMenuItems.reduce((acc, item, idx): number => {
			if (idx < index) {
				acc = (item.children?.length || 0) + acc;
			}
			return acc;
		}, 0);
	};

	const totalParentWithChildrenCount = (index: number) => {
		return wizardMenuItems.filter((item, idx) => idx < index && !!item.children?.length).length;
	};

	React.useEffect(() => {
		findSelectedIndex(current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [current, selectedIndex, flow]);

	logger('WizardMenu > render');

	return { wizardMenuItems, selectedIndex, totalChildrenCount, totalParentWithChildrenCount };
};
